import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

const RESOURCE_CATEGORIES = [
    { id: 'insights', label: 'Product Insights', icon: 'auto_graph' },
    { id: 'cases', label: 'Case Studies', icon: 'rocket_launch' },
    { id: 'docs', label: 'Documentation', icon: 'menu_book' },
    { id: 'security', label: 'Trust & Security', icon: 'verified_user' }
];

const CONTENT = {
    insights: [
        { 
            title: "Scaling Revenue Ops with Unified Data", 
            desc: "Learn how modern scaleups are moving away from fragmented tools to a unified Business OS.",
            readTime: "6 min read",
            category: "Ops Strategy"
        },
        { 
            title: "The Science of Objective Quality Audits", 
            desc: "Why subjective scoring fails and how to implement objective compliance tracking.",
            readTime: "8 min read",
            category: "Quality Control"
        },
        { 
            title: "Automating the Sales Cycle with AI", 
            desc: "A deep dive into how Oblique Automations can save 15+ hours per week per agent.",
            readTime: "5 min read",
            category: "Automation"
        }
    ],
    cases: [
        { 
            title: "Nexus Corp: 40% Increase in Retention", 
            desc: "How a leading SaaS provider used Quality Hub to identify at-risk accounts before they churned.",
            industry: "Enterprise SaaS"
        },
        { 
            title: "Elevate Global: From 15 Apps to 1", 
            desc: "The story of how Elevate Global consolidated their entire operations onto Oblique.",
            industry: "Agency"
        }
    ],
    docs: [
        { title: "Getting Started Guide", desc: "Setting up your workspace, teams, and initial data sync." },
        { title: "API Reference", desc: "Build custom integrations with the Oblique REST API." },
        { title: "Quality Auditor Training", desc: "Best practices for managers performing SOP audits." }
    ],
    security: [
        { title: "SOC 2 Type II Compliance", desc: "Oblique maintains the highest standards for data security and privacy." },
        { title: "Data Encryption", desc: "All data is encrypted at rest and in transit using AES-256 and TLS 1.3." },
        { title: "GDPR & CCPA", desc: "We are fully compliant with global data protection regulations." }
    ]
};

export default function ResourcesPage() {
    const [activeTab, setActiveTab] = useState('insights');

    React.useEffect(() => {
        document.title = "Resources & Insights | Oblique Unified Business OS";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute("content", "Explore Oblique's resources, case studies, and documentation. Learn how to scale your business operations with our unified platform.");
    }, []);

    return (
        <div style={{ background: 'var(--bg-page)', minHeight: '100vh', color: 'var(--text-primary)', fontFamily: "'Lexend Deca', sans-serif" }}>


            {/* Hero Section */}
            <div style={{ 
                background: 'linear-gradient(180deg, var(--bg-surface) 0%, var(--bg-page) 100%)',
                padding: '120px 40px 80px',
                borderBottom: '1px solid var(--border)',
                textAlign: 'center'
            }}>
                <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span style={{ 
                            padding: '6px 12px', 
                            background: 'var(--accent-subtle)', 
                            color: 'var(--accent)', 
                            borderRadius: '100px', 
                            fontSize: '12px', 
                            fontWeight: '500',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}>
                            Knowledge Hub
                        </span>
                        <h1 style={{ fontSize: '56px', fontWeight: '500', margin: '24px 0', letterSpacing: '-0.04em', lineHeight: 1.1 }}>
                            Resources & <span style={{ color: 'var(--accent)' }}>Insights</span>
                        </h1>
                        <p style={{ fontSize: '20px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                            Everything you need to master Oblique and scale your revenue operations with precision.
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* Content Section */}
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '60px 40px' }}>
                {/* Custom Tab Bar */}
                <div style={{ 
                    display: 'flex', 
                    gap: '8px', 
                    background: 'var(--bg-surface)', 
                    padding: '6px', 
                    borderRadius: '16px', 
                    border: '1px solid var(--border)',
                    width: 'fit-content',
                    margin: '0 auto 60px',
                    boxShadow: 'var(--shadow-md)'
                }}>
                    {RESOURCE_CATEGORIES.map(cat => (
                        <button
                            key={cat.id}
                            onClick={() => setActiveTab(cat.id)}
                            style={{
                                padding: '12px 24px',
                                borderRadius: '12px',
                                border: 'none',
                                background: activeTab === cat.id ? 'var(--accent)' : 'transparent',
                                color: activeTab === cat.id ? '#fff' : 'var(--text-secondary)',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <ObliqueIcon name={cat.icon} size={18} />
                            {cat.label}
                        </button>
                    ))}
                </div>

                {/* Tab Panels */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                        style={{ 
                            display: 'grid', 
                            gridTemplateColumns: activeTab === 'insights' ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)', 
                            gap: '32px' 
                        }}
                    >
                        {CONTENT[activeTab].map((item, i) => (
                            <motion.div
                                key={i}
                                whileHover={{ y: -8 }}
                                style={{
                                    background: 'var(--bg-surface)',
                                    borderRadius: '24px',
                                    border: '1px solid var(--border)',
                                    padding: '32px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '16px',
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}
                            >
                                {activeTab === 'insights' && (
                                    <div style={{ fontSize: '11px', fontWeight: '500', color: 'var(--accent)', textTransform: 'uppercase' }}>
                                        {item.category} · {item.readTime}
                                    </div>
                                )}
                                <h3 style={{ fontSize: '20px', fontWeight: '500', lineHeight: 1.3 }}>{item.title}</h3>
                                <p style={{ fontSize: '15px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{item.desc}</p>
                                <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--accent)', fontWeight: '500', fontSize: '14px' }}>
                                    Learn More <ObliqueIcon name="arrow_forward" size={16} />
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Newsletter CTA */}
            <div style={{ maxWidth: '1000px', margin: '80px auto', padding: '0 40px' }}>
                <div style={{ 
                    background: 'var(--text-primary)', 
                    borderRadius: '32px', 
                    padding: '60px', 
                    textAlign: 'center',
                    color: '#fff',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <h2 style={{ fontSize: '32px', fontWeight: '500', marginBottom: '16px', color: '#fff' }}>Stay updated with the latest in Revenue Ops.</h2>
                        <p style={{ opacity: 0.7, marginBottom: '32px' }}>Join 10,000+ operations leaders who get our weekly insights.</p>
                        <div style={{ display: 'flex', gap: '12px', maxWidth: '400px', margin: '0 auto' }}>
                            <input 
                                type="email" 
                                placeholder="name@company.com" 
                                style={{ 
                                    flex: 1, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                                    padding: '16px 20px', borderRadius: '12px', color: '#fff', outline: 'none'
                                }} 
                            />
                            <button style={{ padding: '0 24px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '12px', fontWeight: '500', cursor: 'pointer' }}>
                                Join
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
